import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

const TOUR_CACHE_PREFIX = "s1_product_tour_completed";

function getCacheKey(userId: string): string {
  return `${TOUR_CACHE_PREFIX}_${userId}`;
}

export function useTourStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const forceReset = new URLSearchParams(window.location.search).has("reset_tour");

  const query = useQuery({
    queryKey: ["tour-status", user?.id],
    queryFn: async () => {
      if (forceReset) {
        localStorage.removeItem(getCacheKey(user!.id));
        await supabase.from("users").update({ tour_completed_at: null }).eq("id", user!.id);
        return { tour_completed_at: null };
      }

      const { data, error } = await supabase
        .from("users")
        .select("tour_completed_at")
        .eq("id", user!.id)
        .single();

      if (error) throw error;

      if (data?.tour_completed_at) {
        localStorage.setItem(getCacheKey(user!.id), "true");
      } else if (localStorage.getItem(getCacheKey(user!.id)) === "true") {
        // Backfill : le cache local dit "complete" mais la base non — persister vers la base
        await supabase
          .from("users")
          .update({ tour_completed_at: new Date().toISOString() })
          .eq("id", user!.id);
      }

      return data;
    },
    enabled: !!user,
  });

  const markCompleted = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("users")
        .update({ tour_completed_at: new Date().toISOString() })
        .eq("id", user!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user) localStorage.setItem(getCacheKey(user.id), "true");
      queryClient.invalidateQueries({ queryKey: ["tour-status"] });
    },
  });

  const localCache = user
    ? localStorage.getItem(getCacheKey(user.id)) === "true"
    : false;

  return {
    isCompleted: localCache || !!query.data?.tour_completed_at,
    isLoading: !localCache && query.isLoading,
    markCompleted: markCompleted.mutate,
  };
}
